/**
 * Retry once after 0.5s on transient network/gateway failures.
 * 4xx (except 429) are the caller's problem and are never retried.
 */

const RETRY_DELAY_MS = 500;
const MAX_RETRIES = 1;

function isTransientFailure(error: unknown, status?: number): boolean {
  if (status !== undefined) {
    if (status >= 400 && status < 500 && status !== 429) return false;
    if (status >= 500) return true;
  }
  const message = error instanceof Error ? error.message : String(error);
  return /network|failed to fetch|load failed|timeout|aborted/i.test(message);
}

function getStatusFromError(error: unknown): number | undefined {
  if (error && typeof error === 'object' && typeof (error as { status?: number }).status === 'number') {
    return (error as { status: number }).status;
  }
  return undefined;
}

/** Run `fn`; on a transient failure wait RETRY_DELAY_MS and try once more. */
export async function withRetry<T>(fn: () => Promise<T>): Promise<T> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt += 1) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      if (attempt < MAX_RETRIES && isTransientFailure(error, getStatusFromError(error))) {
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
        continue;
      }
      throw error;
    }
  }
  throw lastError;
}
